import {sleep} from '@benchling/js-utils';
import _ from 'underscore';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {SpreadsheetValidationErrorInfo} from 'bulk-import/SpreadsheetBulkImportConstants';
import {EventConstants} from 'constants/EventConstants';
import {TagSchema} from 'constants/RegistryConstants';
import * as context from 'Context';
import * as FileBrowserDAO from 'file-browser/FileBrowserDAO';
import {BioentitySummary, isSchemaTag, SchemaTag, Tag} from 'folder-items/FileConstants';
import AppDispatcher from 'lib/Dispatcher';
import {AUTOFILL_COMPATIBLE_BIOENTITY_OPTIONS} from 'metadata/AutofillCompatibleBioentityOptions';
import {MixtureMetadataStructuredTable} from 'notebook/integrations/registry/MixtureMetadataStructuredTableAdapter';
import RegistrationErrorsModal, {
  RegistrationErrorsModalProps,
} from 'notebook/integrations/registry/RegistrationErrorsModal';
import {RegistrationStructuredTable} from 'notebook/integrations/registry/RegistrationStructuredTableAdapter';
import {mapNLOInNote} from 'notebook/notes/util/mapNLOInNote';
import AutofillAfterRegistrationModal from 'notebook/registration-tables/AutofillAfterRegistrationModal';
import {RegistrationTable} from 'notebook/registration-tables/RegistrationTableConstants';
import isEntryEditable from 'notebook/store/isEntryEditable';
import isInStore from 'notebook/store/isInStore';
import {ACTION_UPDATE_COMPUTED_TAGS} from 'notebook/store/VersioningConstants';
import {
  getRowAwareLoadingMessage,
  RowSubmissionRange,
  getStructuredTableSubmissionScope,
} from 'notebook/structured-tables/action-functions/makeSubmitRowActionFunction';
import {
  abortAction,
  ActionResultType,
  batchedSubmissionGenerator,
  getSubmissionBatchSize,
  isBatchingRequired,
  ServerSideWriteActionResult,
} from 'notebook/structured-tables/action-functions/PerformActionFunctions';
import {
  ColumnIdentifier,
  getColumnDefinitionIndex,
} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {StructuredTableSubmissionScope} from 'notebook/structured-tables/StructuredTableApiConstants';
import StructuredTableBulkAdapterErrorsModal, {
  StructuredTableBulkAdapterErrorsModalProps,
} from 'notebook/structured-tables/StructuredTableBulkAdapterErrorsModal';
import {
  StructuredTableColumn,
  StructuredTableKind,
} from 'notebook/structured-tables/StructuredTableConstants';
import updateCellWithLiveValue from 'notebook/structured-tables/util/updateCellWithLiveValue';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {EvaluatedCell} from 'notebook/tables/excess/EvaluationConstants';
import {convertTagToTypedValue} from 'notebook/tables/typed-values/DataTypes';
import {BaseTypedValueType} from 'notebook/tables/typed-values/TypedValueConstants';
import {isMixtureMetadataStructuredTable} from 'notebook/TypePredicates';
import {Note, NoteLinkedObject, NoteLinkedObjectType} from 'notebook/util/NoteUtilConstants';
import notifications from 'notifications';
import * as ModelActions from 'redux-stores/ModelActions';
import {getStore} from 'redux-stores/Store';
import * as AutoFillFieldActions from 'registry/AutoFillFieldActions';
import reportUserFacingLockError from 'registry/reportUserFacingLockError';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import legacyRunModal from 'ui-library/legacyRunModal';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import runModal from 'ui-library/runModal';
import * as logging from 'util/Logging';
import requestWithRetries from 'util/requestWithRetries';
import {ArrayElement, assertNever} from 'util/Types';
import * as StageRunActions from 'workflows/StageRunActions';

import isRegistrationStructuredTable from './isRegistrationStructuredTable';

export enum RegistrationActionOrigin {
  REGISTRATION_STRUCTURED_TABLE = 'REGISTRATION_STRUCTURED_TABLE',
  LEGACY_REGISTRATION_TABLE = 'LEGACY_REGISTRATION_TABLE',
  MIXTURE_GROUP = 'MIXTURE_GROUP',
}

/**
 * A RegistrationOriginTable is a note-linked object that contains registration data.
 * The note-linked object can notably be nested inside a group-type note-linked object.
 */
type RegistrationOriginTable<T = RegistrationActionOrigin> =
  T extends RegistrationActionOrigin.REGISTRATION_STRUCTURED_TABLE
    ? RegistrationStructuredTable
    : T extends RegistrationActionOrigin.LEGACY_REGISTRATION_TABLE
      ? RegistrationTable
      : T extends RegistrationActionOrigin.MIXTURE_GROUP
        ? MixtureMetadataStructuredTable
        : never;

const ORIGIN_PREDICATES_BY_TYPE: {
  [K in RegistrationActionOrigin]: (item: NoteLinkedObject) => item is RegistrationOriginTable<K>;
} = {
  [RegistrationActionOrigin.MIXTURE_GROUP]: isMixtureMetadataStructuredTable,
  [RegistrationActionOrigin.REGISTRATION_STRUCTURED_TABLE]: isRegistrationStructuredTable,
  [RegistrationActionOrigin.LEGACY_REGISTRATION_TABLE]: (item: NoteLinkedObject): item is RegistrationTable =>
    item.type === NoteLinkedObjectType.REGISTRATION_TABLE,
};

type RegistrationOriginTableRow<T extends RegistrationOriginTable = RegistrationOriginTable> = ArrayElement<
  T['rows']
>;

function assertNoteLinkedObjectIsRegistrationOriginTable(
  item: NoteLinkedObject
): asserts item is RegistrationOriginTable {
  assert(
    Object.values(ORIGIN_PREDICATES_BY_TYPE).some((matcher) => matcher(item)),
    'Item did not match any known registration origin',
    {item}
  );
}

function getFriendlyNameForActionOrigin(origin: RegistrationActionOrigin): string {
  switch (origin) {
    case RegistrationActionOrigin.MIXTURE_GROUP:
      return 'Mixture group';
    case RegistrationActionOrigin.REGISTRATION_STRUCTURED_TABLE:
      return 'Registration structured table';
    case RegistrationActionOrigin.LEGACY_REGISTRATION_TABLE:
      return 'Legacy registration table';
    default:
      throw Error(`Unexpected registration action origin ${assertNever(origin)}`);
  }
}

export interface RegistrationDAOResponseBase {
  bioentityIds: Array<string>;
  entryVersionId?: number;
  warningMessage?: string;
}

interface RegistrationSubmitResponse extends RegistrationDAOResponseBase {}

enum ResultStatus {
  SUCCESS = 'SUCCESS',
  REGISTRATION_BULK_ADAPTER_ERROR = 'REGISTRATION_BULK_ADAPTER_ERROR',
  OTHER_BULK_ADAPTER_ERROR = 'BULK_ADAPTER_ERROR',
}

type RegistrationActionFunctionResult<DAOResponse extends RegistrationDAOResponseBase> =
  | {
      status: ResultStatus.SUCCESS;
      responseData: DAOResponse;
    }
  // Any non-bulk-adapter errors are thrown as regular exceptions
  | {
      status: ResultStatus.REGISTRATION_BULK_ADAPTER_ERROR;
      bulkAdapterErrorInfo: SpreadsheetValidationErrorInfo;
      canMerge: boolean;
      responseData?: DAOResponse;
    }
  | {
      status: ResultStatus.OTHER_BULK_ADAPTER_ERROR;
      bulkAdapterErrorInfo: SpreadsheetValidationErrorInfo;
      responseData?: DAOResponse;
    };

/**
 * Update the row of a table with computed tag values from fileSummariesById, if the row is finalized.
 */
function addComputedTagValueToRow<R extends RegistrationOriginTableRow>(
  row: R,
  columnIdentifiers: Array<ColumnIdentifier>,
  fileSummariesById: {[id: string]: BioentitySummary},
  provenanceUuid: string
): R {
  if (!row.isFinalized) {
    return row;
  }
  return {
    ...row,
    cells: row.cells.map((cell, i) => {
      const columnIdentifier = columnIdentifiers[i];
      if (columnIdentifier.type !== TableColumnType.TAG_VALUE) {
        return cell;
      }

      // Find the row's bioentity in fileSummariesById
      const bioentityColumnIndex = getColumnDefinitionIndex({
        columnDefinitionType: TableColumnType.EXISTING_BIOENTITY,
        tableColumnDefinitions: columnIdentifiers,
      });
      const bioentityCell = row.cells[bioentityColumnIndex];
      if (!bioentityCell.typedValue) {
        return cell;
      }
      assert(
        bioentityCell.typedValue.type === BaseTypedValueType.BIOENTITY,
        'Expected EXISTING_BIOENTITY column to have a bioentity typed value',
        {bioentityCell, provenanceUuid}
      );
      const bioentityTypedValue = bioentityCell.typedValue;
      const bioentityId = bioentityTypedValue.bioentitySummary && bioentityTypedValue.bioentitySummary.id;
      if (bioentityId == null || fileSummariesById[bioentityId] == null) {
        return cell;
      }
      const bioentity = fileSummariesById[bioentityId];

      const bioentityTag = bioentity.tags.find(
        (tag: Tag) => isSchemaTag(tag) && tag.tagSchemaField.apiId === columnIdentifier.tagSchemaFieldApiId
      ) as SchemaTag | undefined;
      if (!bioentityTag?.tagSchemaField?.isComputed) {
        return cell;
      }
      return updateCellWithLiveValue(cell, convertTagToTypedValue(bioentityTag));
    }),
  };
}

export function addComputedTagValuesToNote<T extends Note>(
  note: T,
  provenanceUuid: string,
  fileSummariesById: {[id: string]: BioentitySummary}
): T {
  return mapNLOInNote(note, (item: NoteLinkedObject) =>
    _mapComputedTagsIfRegistrationOriginTable(item, provenanceUuid, fileSummariesById)
  );
}

function _mapComputedTagsIfRegistrationOriginTable(
  item: NoteLinkedObject,
  provenanceUuid: string,
  fileSummariesById: {[id: string]: BioentitySummary}
): NoteLinkedObject {
  if ((item as RegistrationOriginTable).provenanceUuid !== provenanceUuid) {
    return item;
  }
  assertNoteLinkedObjectIsRegistrationOriginTable(item);
  const columnIdentifiers: Array<ColumnIdentifier> = item.columns.map((column: StructuredTableColumn) => ({
    type: column.columnType,
    columnGroup: column.columnGroup,
    tagSchemaFieldApiId: column.schemaFieldApiId,
  }));
  return stubbableFunctions.addComputedTagValuesToTable(item, columnIdentifiers, fileSummariesById);
}

async function runBulkAutofillByFieldTypes({
  selectedFieldTypes,
  sequenceIds,
}: {
  selectedFieldTypes: Array<FieldType>;
  sequenceIds: Array<string>;
}): Promise<void> {
  const selectedFieldTypesSet = new Set(selectedFieldTypes);
  const autofillPromises: Array<Promise<void>> = [];
  for (const option of AUTOFILL_COMPATIBLE_BIOENTITY_OPTIONS) {
    if (!selectedFieldTypesSet.has(option.fieldType)) {
      continue;
    }
    switch (option.name) {
      case 'Parts':
        autofillPromises.push(AutoFillFieldActions.bulkAutoFillPartFields(sequenceIds));
        break;
      case 'Transcriptions':
        autofillPromises.push(AutoFillFieldActions.bulkAutofillTranscriptions(sequenceIds));
        break;
      case 'Translations':
        autofillPromises.push(AutoFillFieldActions.bulkAutofillTranslations(sequenceIds));
        break;
      default:
        throw Error(`Unexpected autofill option ${assertNever(option.name)}`);
    }
  }

  await Promise.all(autofillPromises);
}

export async function maybePromptAutofillAfterRegistration({
  bioentityIds,
  tagSchema,
}: {
  bioentityIds: Array<string>;
  tagSchema: TagSchema;
}): Promise<void> {
  if (!BenchlingConfig.MOLBIO_ENABLE_AUTOFILL_IN_REG_TABLES) {
    return;
  }

  const availableAutofillOptions = AUTOFILL_COMPATIBLE_BIOENTITY_OPTIONS.filter((autofillOption) =>
    tagSchema.fields.some((field) => field.fieldType === autofillOption.fieldType)
  );
  if (availableAutofillOptions.length === 0) {
    return;
  }

  const sequenceIds = _.unique(bioentityIds);
  if (sequenceIds.length === 0) {
    return;
  }

  const selectedFieldTypes = await LoadingScreen.withLoadingScreenHidden(() =>
    runModal(
      AutofillAfterRegistrationModal,
      {
        availableAutofillOptions,
      },
      {
        title: 'Autofill sequence fields',
      }
    )
  );
  if (selectedFieldTypes == null || selectedFieldTypes.length === 0) {
    return;
  }

  Promise.resolve(runBulkAutofillByFieldTypes({selectedFieldTypes, sequenceIds})).catch((error) => {
    notifications.notifyError(error);
  });
}

export async function triggerComputedFieldsUpdate({
  entryId,
  provenanceUuid,
  bioentityIds,
}: {
  entryId: string;
  provenanceUuid: string;
  bioentityIds: Array<string>;
}): Promise<void> {
  if (bioentityIds.length === 0) {
    return;
  }
  const fileSummariesById = await StageRunActions.updateComputedTags(
    // As part of updating computed tags, we re-fetch file summaries
    // from the endpoint `files:bulk-get-summaries`.
    // That endpoint will error if we make a request with duplicate IDs.
    // eslint-disable-next-line no-restricted-syntax
    _.uniq(bioentityIds)
  );
  if (fileSummariesById != null) {
    while (!isEntryEditable(entryId)) {
      if (!isInStore(entryId)) {
        return;
      }
      await sleep(1000);
    }
    context.using({immediateAutoSaverQueueSaveAction: ACTION_UPDATE_COMPUTED_TAGS}, null, () => {
      AppDispatcher.dispatch({
        actionType: EventConstants.COMPUTED_TAGS_UPDATED_AFTER_REGISTRATION_ACTION,
        entryId,
        fileSummariesById,
        provenanceUuid,
      });
    });
  }
}

export async function doPostRegistrationRefresh<DAOResponse extends RegistrationDAOResponseBase>(
  registrationSubmitResponse: DAOResponse | RegistrationActionFunctionResult<DAOResponse>,
  entryOrTemplateId: string,
  provenanceUuid: string,
  tagSchema?: TagSchema
): Promise<void> {
  const bioentityIds =
    (registrationSubmitResponse as RegistrationActionFunctionResult<DAOResponse>).responseData !== undefined
      ? (registrationSubmitResponse as RegistrationActionFunctionResult<DAOResponse>).responseData!
          .bioentityIds
      : (registrationSubmitResponse as RegistrationDAOResponseBase).bioentityIds;
  // Fetch the updated files into the client store so we don't show a stale indicator
  const response = await FileBrowserDAO.bulkFetchFileSummaries(
    // The endpoint errors if we make a request with duplicate IDs.
    // eslint-disable-next-line no-restricted-syntax
    _.uniq(bioentityIds)
  );
  const fileSummariesById = response?.fileSummariesById;
  if (fileSummariesById) {
    getStore().dispatch(ModelActions.filesFetchCompleted(fileSummariesById));
    if (tagSchema != null) {
      await maybePromptAutofillAfterRegistration({
        bioentityIds,
        tagSchema,
      });
    }
  }
  // And now check for computed fields asynchronously
  triggerComputedFieldsUpdate({
    entryId: entryOrTemplateId,
    provenanceUuid,
    bioentityIds,
  });
}

async function attemptRegistration<DAOResponse extends RegistrationDAOResponseBase>(
  registerDAOFn: (
    allowMerging: boolean,
    selection?: RowSubmissionRange,
    computedTable?: Array<Array<EvaluatedCell>>,
    latestVersionId?: number,
    submissionScope?: StructuredTableSubmissionScope
  ) => PromiseLike<DAOResponse> | DAOResponse,
  shouldMerge: boolean = false,
  selection: RowSubmissionRange | undefined,
  computedTable: Array<Array<EvaluatedCell>> | undefined,
  latestVersionId: number | undefined,
  submissionScope: StructuredTableSubmissionScope
): Promise<RegistrationActionFunctionResult<DAOResponse>> {
  try {
    const responseData = await requestWithRetries(
      () => registerDAOFn(shouldMerge, selection, computedTable, latestVersionId, submissionScope),
      {
        onMaxAttemptsExceeded: () => {
          reportUserFacingLockError('client:registerActionFunction');
        },
      }
    );
    return {status: ResultStatus.SUCCESS, responseData};
  } catch (e) {
    if (e.responseJSON && e.responseJSON.bulkAdapterErrorInfo) {
      const {bulkAdapterErrorInfo} = e.responseJSON;
      const isRegistrationError = e.responseJSON.hasOwnProperty('canMerge');
      if (isRegistrationError) {
        return {
          status: ResultStatus.REGISTRATION_BULK_ADAPTER_ERROR,
          bulkAdapterErrorInfo,
          canMerge: Boolean(e.responseJSON.canMerge),
        };
      } else {
        return {
          status: ResultStatus.OTHER_BULK_ADAPTER_ERROR,
          bulkAdapterErrorInfo,
        };
      }
    } else {
      throw e;
    }
  }
}

// Note that this is only used for registration via a modern registration table or a mixture group; legacy
// registration tables user their own logic (in registerSamples.ts)
export async function runRegistrationFlow<DAOResponse extends RegistrationDAOResponseBase>(
  registerDAOFunction: (
    allowMerging: boolean,
    selection?: RowSubmissionRange,
    computedTable?: Array<Array<EvaluatedCell>>,
    latestVersionId?: number,
    submissionScope?: StructuredTableSubmissionScope
  ) => PromiseLike<DAOResponse> | DAOResponse,
  tagSchema: TagSchema,
  origin: RegistrationActionOrigin,
  selection?: RowSubmissionRange,
  structuredTable?: RegistrationStructuredTable,
  computedTable?: Array<Array<EvaluatedCell>>,
  updateStatus?: (msg: string) => void
): Promise<ServerSideWriteActionResult<DAOResponse>> {
  const submissionScope = getStructuredTableSubmissionScope(selection);
  const batchSize =
    structuredTable && structuredTable.kind === StructuredTableKind.REGISTRATION
      ? getSubmissionBatchSize(StructuredTableKind.REGISTRATION)
      : null;

  if (isBatchingRequired(computedTable!, batchSize, selection)) {
    let latestVersionId: number | undefined;
    let partialSuccess = false;
    const responseData: RegistrationSubmitResponse = {
      bioentityIds: [],
      entryVersionId: -1,
    };
    let submittedRange: RowSubmissionRange = {
      start: selection ? selection.start : 0,
      end: selection ? selection.start : 0,
    };
    const batches = batchedSubmissionGenerator(computedTable!, batchSize, structuredTable!, selection);

    try {
      for (const {range, evaluatedRowBatch} of batches) {
        if (updateStatus) {
          updateStatus(getRowAwareLoadingMessage(range, 'Registering rows', 'Registering row'));
        }
        /*
         * Initial registration attempt
         */
        const initialBatchResult = await attemptRegistration(
          registerDAOFunction,
          false,
          range,
          evaluatedRowBatch,
          latestVersionId,
          submissionScope
        );
        if (initialBatchResult.status === ResultStatus.SUCCESS) {
          latestVersionId = initialBatchResult.responseData.entryVersionId;
          submittedRange = {...submittedRange, end: range.end};
          partialSuccess = true;
          responseData.bioentityIds.push(...initialBatchResult.responseData.bioentityIds);
          if (initialBatchResult.responseData.hasOwnProperty('warningMessage')) {
            notifications.warn(initialBatchResult.responseData.warningMessage!);
          }
        } else {
          /*
           * Otherwise it's a bulk adapter error. Show errors, and if it's for registration, figure out whether to
           * make another attempt.
           */
          const {bulkAdapterErrorInfo} = initialBatchResult;
          if (initialBatchResult.status === ResultStatus.REGISTRATION_BULK_ADAPTER_ERROR) {
            const shouldMerge = await LoadingScreen.withLoadingScreenHidden(() =>
              legacyRunModal<RegistrationErrorsModalProps>(RegistrationErrorsModal, {
                tagSchemaId: tagSchema.id,
                errorData: bulkAdapterErrorInfo,
                canMerge: initialBatchResult.canMerge,
              })
            );
            if (!shouldMerge) {
              if (partialSuccess) {
                return {
                  resultType: ActionResultType.PARTIAL_SUCCESS,
                  responseData: {...initialBatchResult.responseData!, responseData},
                  submittedRange,
                };
              }
              return abortAction();
            }
          } else {
            legacyRunModal<StructuredTableBulkAdapterErrorsModalProps>(
              StructuredTableBulkAdapterErrorsModal,
              {
                tagSchemaId: tagSchema.id,
                errorData: bulkAdapterErrorInfo,
              }
            );
            if (partialSuccess) {
              return {
                resultType: ActionResultType.PARTIAL_SUCCESS,
                responseData: {...initialBatchResult.responseData!, responseData},
                submittedRange,
              };
            }
            return abortAction();
          }

          const UNEXPECTED_ERROR_SENTRY_MESSAGE =
            `${getFriendlyNameForActionOrigin(origin)} submission: ` +
            'We told the user we could fix the errors from their first submission, and merge.' +
            'But we errored again!';

          /*
           * Re-attempt registration with merging enabled, if the user chose to merge
           */
          let batchResultAfterMerge: RegistrationActionFunctionResult<DAOResponse>;
          try {
            batchResultAfterMerge = await attemptRegistration(
              registerDAOFunction,
              true,
              range,
              evaluatedRowBatch,
              latestVersionId,
              submissionScope
            );
          } catch (e) {
            logging.error(UNEXPECTED_ERROR_SENTRY_MESSAGE, {
              extra: {e: e.responseJSON?.bulkAdapterErrorInfo ?? e},
            });
            throw e;
          }

          if (batchResultAfterMerge.status === ResultStatus.SUCCESS) {
            if (batchResultAfterMerge.responseData.hasOwnProperty('warningMessage')) {
              notifications.warn(batchResultAfterMerge.responseData.warningMessage!);
            }
            latestVersionId = batchResultAfterMerge.responseData.entryVersionId;
            submittedRange = {...submittedRange, end: range.end};
            partialSuccess = true;
            responseData.bioentityIds.push(...batchResultAfterMerge.responseData.bioentityIds);
          } else {
            // The bulk adapter errored again.
            // We can show the errors to the user, but this is unexpected.
            // The user should have been blocked from selecting "Merge and register".
            logging.error(UNEXPECTED_ERROR_SENTRY_MESSAGE, {extra: {batchResultAfterMerge}});
            await LoadingScreen.withLoadingScreenHidden(() =>
              legacyRunModal<RegistrationErrorsModalProps>(RegistrationErrorsModal, {
                tagSchemaId: tagSchema.id,
                errorData: batchResultAfterMerge.bulkAdapterErrorInfo,
                canMerge: false,
              })
            );
            if (partialSuccess) {
              return {
                resultType: ActionResultType.PARTIAL_SUCCESS,
                responseData: {...batchResultAfterMerge.responseData!, responseData},
                submittedRange,
              };
            }
            return abortAction();
          }
        }
      }
    } catch (e) {
      if (partialSuccess) {
        notifications.error(notifications.getFriendlyError(e) ?? 'An unexpected error occured.');
        return {
          resultType: ActionResultType.PARTIAL_SUCCESS,
          responseData: responseData as DAOResponse,
          submittedRange,
        };
      }

      throw e;
    }

    return {resultType: ActionResultType.SUCCESS, responseData: responseData as DAOResponse};
  }
  /*
   * Initial registration attempt
   */
  const initialRegistrationResult = await attemptRegistration(
    registerDAOFunction,
    false,
    selection,
    computedTable,
    undefined,
    submissionScope
  );
  if (initialRegistrationResult.status === ResultStatus.SUCCESS) {
    const {responseData} = initialRegistrationResult;
    if (responseData.hasOwnProperty('warningMessage')) {
      notifications.warn(responseData.warningMessage!);
    }
    return {
      resultType: ActionResultType.SUCCESS,
      responseData: initialRegistrationResult.responseData,
    };
  }

  /*
   * Otherwise it's a bulk adapter error. Show errors, and if it's for registration, figure out whether to
   * make another attempt.
   */
  const {bulkAdapterErrorInfo} = initialRegistrationResult;
  if (initialRegistrationResult.status === ResultStatus.REGISTRATION_BULK_ADAPTER_ERROR) {
    const shouldMerge = await LoadingScreen.withLoadingScreenHidden(() =>
      legacyRunModal<RegistrationErrorsModalProps>(RegistrationErrorsModal, {
        tagSchemaId: tagSchema.id,
        errorData: bulkAdapterErrorInfo,
        canMerge: initialRegistrationResult.canMerge,
      })
    );
    if (!shouldMerge) {
      return abortAction();
    }
  } else {
    legacyRunModal<StructuredTableBulkAdapterErrorsModalProps>(StructuredTableBulkAdapterErrorsModal, {
      tagSchemaId: tagSchema.id,
      errorData: bulkAdapterErrorInfo,
    });
    return abortAction();
  }

  const UNEXPECTED_ERROR_SENTRY_MESSAGE =
    `${getFriendlyNameForActionOrigin(origin)} submission: ` +
    'We told the user we could fix the errors from their first submission, and merge.' +
    'But we errored again!';

  /*
   * Re-attempt registration with merging enabled, if the user chose to merge
   */
  let registrationResultAfterMerge: RegistrationActionFunctionResult<DAOResponse>;
  try {
    registrationResultAfterMerge = await attemptRegistration(
      registerDAOFunction,
      true,
      selection,
      computedTable,
      undefined,
      submissionScope
    );
  } catch (e) {
    logging.error(UNEXPECTED_ERROR_SENTRY_MESSAGE, {extra: {e: e.responseJSON?.bulkAdapterErrorInfo ?? e}});
    throw e;
  }

  if (registrationResultAfterMerge.status === ResultStatus.SUCCESS) {
    const {responseData} = registrationResultAfterMerge;
    if (responseData.hasOwnProperty('warningMessage')) {
      notifications.warn(responseData.warningMessage!);
    }
    return {
      resultType: ActionResultType.SUCCESS,
      responseData: registrationResultAfterMerge.responseData,
    };
  } else {
    // The bulk adapter errored again.
    // We can show the errors to the user, but this is unexpected.
    // The user should have been blocked from selecting "Merge and register".
    logging.error(UNEXPECTED_ERROR_SENTRY_MESSAGE, {extra: {registrationResultAfterMerge}});
    await LoadingScreen.withLoadingScreenHidden(() =>
      legacyRunModal<RegistrationErrorsModalProps>(RegistrationErrorsModal, {
        tagSchemaId: tagSchema.id,
        errorData: registrationResultAfterMerge.bulkAdapterErrorInfo,
        canMerge: false,
      })
    );
    return abortAction();
  }
}

export const stubbableFunctions = {
  /**
   * Update the finalized rows of a table with computed tag values from fileSummariesById
   */
  addComputedTagValuesToTable<TTable extends RegistrationOriginTable>(
    tableForRegistration: TTable,
    columnIdentifiers: Array<ColumnIdentifier>,
    fileSummariesById: {[id: string]: BioentitySummary}
  ): TTable {
    const tableRows = tableForRegistration.rows as Array<RegistrationOriginTableRow>;
    return {
      ...tableForRegistration,
      rows: tableRows.map((row) =>
        addComputedTagValueToRow(
          row,
          columnIdentifiers,
          fileSummariesById,
          tableForRegistration.provenanceUuid
        )
      ),
    };
  },
};
